import { RHFSelectInput } from "@impero-com/common/components/form/rhfFields/rhfSelectInput";
import { ERRORED_CLASSNAME } from "@impero-com/common/utils/form/erroredElements";
import { assertNever } from "@impero-com/common/utils/obj";
import { FieldError, Tooltip } from "@impero-com/ui";
import { Checkbox, Typography } from "antd";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import { Control, useController, useWatch } from "react-hook-form";
import { z } from "zod";

import { FieldKey, parseFieldKey } from "../fields";
import { FormSchema } from "../schema";
import * as theme from "../theme.css";

export const conditionSchema = z.object({
    action: z.literal(["Show", "Hide"]),
    operator: z.literal(["Equal", "NotEqual"]),
    columnId: z.guid(),
    value: z.guid(),
});
export type FormCondition = z.infer<typeof conditionSchema>;

export type ConditionKey = `${FieldKey}.condition`;
interface StructureFieldConditionProps {
    fieldKey: FieldKey;
    control: Control<FormSchema>;
}
export function StructureFieldCondition({
    fieldKey,
    control,
}: StructureFieldConditionProps) {
    const { field: conditionField, fieldState } = useController({
        name: `${fieldKey}.condition`,
        control,
    });
    const requiredForAssessment = useWatch({
        name: `${fieldKey}.requiredForAssessment`,
        control,
    });
    const isChecked = conditionField.value !== null;
    const handleConditionCheck = (e: CheckboxChangeEvent) => {
        // Not exactly smart to use an object as a checkbox input value
        if (e.target.checked) {
            conditionField.onChange({
                action: "Show",
                operator: "Equal",
                fieldId: null,
                valueId: null,
            });
        } else {
            conditionField.onChange(null);
        }
    };
    const disableCheckbox = !isChecked && requiredForAssessment;
    const checkbox = (
        <Checkbox
            checked={isChecked}
            onChange={handleConditionCheck}
            value={isChecked}
            disabled={disableCheckbox}
        >
            Enable condition
        </Checkbox>
    );
    return (
        <div className={fieldState.invalid ? ERRORED_CLASSNAME : ""}>
            {disableCheckbox ? (
                <Tooltip title="A field required for assessment cannot have a condition">
                    {checkbox}
                </Tooltip>
            ) : (
                checkbox
            )}
            {isChecked && (
                <StructureConditions
                    control={control}
                    conditionKey={`${fieldKey}.condition`}
                    fieldKey={fieldKey}
                />
            )}
            <FieldError
                error={
                    fieldState.error?.message ?? fieldState.error?.root?.message
                }
            />
        </div>
    );
}

interface StructureConditionsProps {
    control: Control<FormSchema>;
    conditionKey: ConditionKey;
    fieldKey: FieldKey;
}

function StructureConditions({
    control,
    conditionKey,
    fieldKey,
}: StructureConditionsProps) {
    // TODO: Perhaps allFields and allOptions computations can be computed once at the root and passed down the props
    const { sectionIndex, subsectionIndex, fieldIndex } =
        parseFieldKey(fieldKey);
    const sections = useWatch({ name: "sections", control });
    const conditionalColumnId = useWatch({
        name: `${conditionKey}.columnId`,
        control,
    });
    const sectionLevel = useWatch({
        name: `sections.${sectionIndex}.level`,
        control,
    });
    const allFields = sections.flatMap((section, sectionIndex) => {
        return section.subsections.flatMap((subsection, subsectionIndex) => {
            return subsection.fields.map((field, fieldIndex) => {
                return {
                    sectionIndex,
                    subsectionIndex,
                    fieldIndex,
                    fieldName: field.name,
                    fieldId: field.fieldId,
                    fieldType: field.fieldType.type,
                    sectionLevel: section.level,
                };
            });
        });
    });
    const allOptions = sections.flatMap((section) => {
        return section.subsections.flatMap((subsection) => {
            return subsection.fields.flatMap((field) => {
                switch (field.fieldType.type) {
                    case "RadioButtonGroup":
                    case "CheckboxGroup":
                    case "Dropdown":
                        return field.fieldType.options.map((option) => {
                            return {
                                value: option.value,
                                optionId: option.optionId,
                                fieldId: field.fieldId,
                            };
                        });
                    case "Heading":
                    case "TextArea":
                    case "TextInput":
                    case "NumericInput":
                    case "AttachedFiles":
                        return [];
                    default:
                        return assertNever(field.fieldType);
                }
            });
        });
    });
    const fields = allFields
        .filter(
            (field) =>
                (field.fieldType === "Dropdown" ||
                    field.fieldType === "RadioButtonGroup") &&
                field.sectionLevel === sectionLevel &&
                (field.sectionIndex < sectionIndex ||
                    (field.sectionIndex === sectionIndex &&
                        field.subsectionIndex < subsectionIndex) ||
                    (field.sectionIndex === sectionIndex &&
                        field.subsectionIndex === subsectionIndex &&
                        field.fieldIndex < fieldIndex))
        )
        .map((field) => ({
            label: `${field.fieldName} (${field.fieldId})`,
            value: field.fieldId,
        }));
    const optionValueOptions = allOptions
        .filter((optionValue) => optionValue.fieldId === conditionalColumnId)
        .map((optionValue) => ({
            label: optionValue.value,
            value: optionValue.optionId,
        }));

    return (
        <div className={theme.condition}>
            <RHFSelectInput
                control={control}
                name={`${conditionKey}.action`}
                placeholder="Action"
                options={[
                    { label: "Show", value: "Show" },
                    { label: "Hide", value: "Hide" },
                ]}
            />
            <Typography.Text>If</Typography.Text>
            <RHFSelectInput
                control={control}
                name={`${conditionKey}.columnId`}
                placeholder="Field"
                options={fields}
                dropdownMatchSelectWidth={false}
            />
            <RHFSelectInput
                control={control}
                name={`${conditionKey}.operator`}
                options={[
                    { label: "Is equal to", value: "Equal" },
                    { label: "Is not equal to", value: "NotEqual" },
                ]}
            />
            <RHFSelectInput
                control={control}
                name={`${conditionKey}.value`}
                placeholder="Value"
                options={optionValueOptions}
                dropdownMatchSelectWidth={false}
            />
        </div>
    );
}
